import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import FeeReceiptPrint from '../FeeCollection/FeeReceiptPrint';
import { useCatchResponse } from '../../utils/useCatchResponse';
import UploadFile from '../UploadFile/UploadFile';
import moment from 'moment';
import './FeePayment.css';
import '../../components/FeeCollection/FeeReceipt.css';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Load Razorpay's checkout.js on demand (only when a payment is started) so it
// isn't pulled on every page. Reuses an in-flight/existing load.
const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => reject(new Error('Failed to load payment gateway')));
        return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load payment gateway'));
    document.body.appendChild(script);
});

const FeePayment = () => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState('');
    const [schoolId, setSchoolId] = useState('');
    const [financialYear, setFinancialYear] = useState('');
    const [childId, setChildId] = useState('');
    const [feeData, setFeeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAllocations, setSelectedAllocations] = useState([]);
    const [showManualPay, setShowManualPay] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [receiptPaymentId, setReceiptPaymentId] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [paymentHistory, setPaymentHistory] = useState([]);

    // Manual payment form
    const [paymentDate, setPaymentDate] = useState(new Date());
    const [remarks, setRemarks] = useState('');
    const [proofFile, setProofFile] = useState(null);
    const [proofPreview, setProofPreview] = useState(null);
    const proofUploadRef = useRef(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const loginData = CommonConfig.loginData();
        const userId = loginData?._id;
        const schoolid = loginData?.CreatedBy || '';
        const role = loginData?.UserRole || '';
        // Parent picks a child; a Student is the subject themselves.
        const selectedChild = loginData?.SelectedChildData;
        const studentId = role === 'Parent'
            ? (selectedChild?._id || '')
            : (role === 'Student' ? userId : '');

        setSecurityUserID(userId);
        setSchoolId(schoolid);
        setChildId(studentId);

        const years = await CommonConfig.getFinancialYears(schoolid);
        const currentYear = CommonConfig.getCurrentFinancialYear(years);
        const selectedAcYear = CommonConfig.getCookie('selectedAcademicYear');
        const fy = selectedAcYear ?? currentYear?.value;
        setFinancialYear(fy);

        if (studentId) {
            fetchFeeDetail(studentId, schoolid, fy);
        } else {
            setLoading(false);
        }
    };

    const fetchFeeDetail = async (studentId, schoolid, fy) => {
        try {
            setLoading(true);
            const response = await api.post(APIConstant.path.GetParentFeeDetail, {
                StudentId: studentId,
                SchoolId: schoolid,
                FinancialYearId: fy,
            });
            if (response.success) {
                setFeeData(response.data);
            }
            setLoading(false);
            Loader.hide();
        } catch (err) {
            setLoading(false);
            Loader.hide();
            catchResponse(err);
        }
    };

    const fetchPaymentHistory = async () => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetStudentPaymentHistory, {
                StudentId: childId,
                SchoolId: schoolId,
                FinancialYearId: financialYear,
            });
            if (response.success) {
                setPaymentHistory(response.data || []);
                setShowHistory(true);
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const handleManualPayment = () => {
        if (selectedAllocations.length === 0) {
            Toast.error({ message: 'Please select fees to pay' });
            return;
        }
        setPaymentDate(new Date());
        setRemarks('');
        setProofFile(null);
        setProofPreview(null);
        setShowManualPay(true);
    };

    const handleOnlinePayment = async () => {
        if (selectedAllocations.length === 0) {
            Toast.error({ message: 'Please select fees to pay' });
            return;
        }

        const totalAmount = selectedAllocations.reduce((sum, a) => sum + (a.Outstanding || 0), 0);

        const paymentItems = selectedAllocations.map(a => ({
            AllocationId: a._id,
            FeeTypeName: a.FeeTypeName,
            Amount: a.Outstanding - (a.Fine || 0),
            Fine: a.Fine || 0,
        }));

        // Create Razorpay order
        Loader.show();
        try {
            await loadRazorpayScript();
        } catch (e) {
            Loader.hide();
            Toast.error({ message: 'Failed to load payment gateway. Please try again.' });
            return;
        }
        api.post(APIConstant.path.CreateRazorpayOrder, {
            SchoolId: schoolId,
            StudentId: childId,
            Amount: totalAmount,
            PaymentItems: paymentItems,
            FinancialYear: financialYear,
        }).then(response => {
            Loader.hide();
            if (!response.success) {
                Toast.error({ message: response.message });
                return;
            }

            const { orderId, amount, keyId } = response.data;

            const options = {
                key: keyId,
                amount: amount,
                currency: 'INR',
                name: feeData?.StudentInfo?.StudentName || 'Fee Payment',
                description: 'School Fee Payment',
                order_id: orderId,
                handler: function (razorpayResponse) {
                    verifyPayment(razorpayResponse, paymentItems, totalAmount);
                },
                prefill: {
                    name: feeData?.StudentInfo?.StudentName || '',
                    contact: feeData?.StudentInfo?.ContactNo || '',
                },
                theme: { color: '#1C515E' },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        }).catch(err => {
            Loader.hide();
            catchResponse(err);
        });
    };

    const verifyPayment = async (razorpayResponse, paymentItems, totalAmount) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.VerifyRazorpayPayment, {
                SchoolId: schoolId,
                StudentId: childId,
                RazorpayOrderId: razorpayResponse.razorpay_order_id,
                RazorpayPaymentId: razorpayResponse.razorpay_payment_id,
                RazorpaySignature: razorpayResponse.razorpay_signature,
                PaymentItems: paymentItems,
                TotalAmount: totalAmount,
                NetAmount: totalAmount,
                FinancialYear: financialYear,
                CreatedBy: securityUserID,
            });

            if (response.success) {
                Toast.success({ message: `Payment successful! Receipt No: ${response.ReceiptNo}` });
                setSelectedAllocations([]);
                setReceiptPaymentId(response.data?._id);
                setShowReceipt(true);
                fetchFeeDetail(childId, schoolId, financialYear);
            } else {
                Toast.error({ message: response.message });
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const submitManualPayment = async () => {
        if (!proofFile) {
            Toast.error({ message: 'Please upload payment proof' });
            return;
        }

        try {
            setSaving(true);
            Loader.show();

            const totalAmount = selectedAllocations.reduce((sum, a) => sum + (a.Outstanding || 0), 0);
            const paymentItems = selectedAllocations.map(a => ({
                AllocationId: a._id,
                FeeTypeName: a.FeeTypeName,
                Amount: a.Outstanding - (a.Fine || 0),
                Fine: a.Fine || 0,
            }));

            const formData = new FormData();
            formData.append('StudentId', childId);
            formData.append('PaymentDate', paymentDate.toISOString());
            formData.append('TotalAmount', totalAmount);
            formData.append('NetAmount', totalAmount);
            formData.append('Remarks', remarks);
            formData.append('PaymentItems', JSON.stringify(paymentItems));
            formData.append('FinancialYear', financialYear);
            formData.append('SchoolId', schoolId);
            formData.append('CreatedBy', securityUserID);
            formData.append('ManualProofImage', proofFile);

            const response = await fetch(`${apiBase}/feeCollection/submitManualPayment`, {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();

            if (result.success) {
                Toast.success({ message: result.message });
                setShowManualPay(false);
                setSelectedAllocations([]);
                fetchFeeDetail(childId, schoolId, financialYear);
            } else {
                Toast.error({ message: result.message });
            }
            setSaving(false);
            Loader.hide();
        } catch (err) {
            setSaving(false);
            Loader.hide();
            catchResponse(err);
        }
    };

    if (loading) {
        return (
            <div className='wayment-owner-dashboard et-master-page'>
                <div style={{ textAlign: 'center', padding: '60px' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#1C515E' }}></i>
                </div>
            </div>
        );
    }

    if (!childId) {
        return (
            <div className='wayment-owner-dashboard et-master-page'>
                <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
                    <i className="pi pi-user" style={{ fontSize: '3rem', marginBottom: '16px', display: 'block', color: '#ccc' }}></i>
                    Please select a child to view fees
                </div>
            </div>
        );
    }

    if (!feeData) {
        return (
            <div className='wayment-owner-dashboard et-master-page'>
                <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>No fee data found</div>
            </div>
        );
    }

    const { StudentInfo, Summary, FeeGroups, PaymentConfig } = feeData;
    const totalSelected = selectedAllocations.reduce((sum, a) => sum + (a.Outstanding || 0), 0);

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-wallet"></i>
                    <h2>Fee Payment</h2>
                </div>
                <Button icon="pi pi-history" label="Payment History" className="p-button-sm p-button-outlined" onClick={fetchPaymentHistory} />
            </div>

            {/* Student Info & Summary */}
            <div className="fp-student-header">
                <div className="fp-student-info">
                    {StudentInfo.ProfilePic && (
                        <div className="fp-photo">
                            <img src={`${apiBase}/UserProfilePic/${StudentInfo.ProfilePic}`} alt=""
                                onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                    )}
                    <div>
                        <h3 style={{ margin: 0, color: '#1C515E' }}>{StudentInfo.StudentName}</h3>
                        <p style={{ margin: '4px 0', color: '#666', fontSize: '13px' }}>
                            Class - {StudentInfo.ClassName} | Board - {StudentInfo.BoardName} | Roll No. - {StudentInfo.RollNo}
                        </p>
                    </div>
                </div>
                <div className="fp-summary-cards">
                    {[
                        { label: 'Annual Fee', value: Summary.AnnualFee, color: '#1C515E' },
                        { label: 'Fee Paid', value: Summary.FeePaid, color: '#2d8a4e' },
                        { label: 'Outstanding', value: Summary.Outstanding, color: '#c53030' },
                    ].map((card, i) => (
                        <div key={i} className="fp-summary-card" style={{ borderLeftColor: card.color }}>
                            <div style={{ fontSize: '11px', color: '#666', fontWeight: 600 }}>{card.label}</div>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: card.color }}>&#8377; {card.value?.toLocaleString('en-IN') || 0}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Fee Groups */}
            {FeeGroups?.map((group, gIndex) => (
                <Accordion key={gIndex} className="p-mb-3 p-mx-3">
                    <AccordionTab header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <span>{group.StructureName}</span>
                            <span style={{ fontSize: '13px', color: '#666' }}>&#8377; {group.TotalAmount?.toLocaleString('en-IN')}</span>
                        </div>
                    }>
                        <DataTable value={group.Allocations} size="small" className="custom-table"
                            selection={selectedAllocations.filter(a => group.Allocations.some(ga => ga._id === a._id))}
                            onSelectionChange={(e) => {
                                const otherGroupSelections = selectedAllocations.filter(a => !group.Allocations.some(ga => ga._id === a._id));
                                const filtered = (e.value || []).filter(a => a.Status !== 'Paid');
                                setSelectedAllocations([...otherGroupSelections, ...filtered]);
                            }}
                            dataKey="_id" selectionMode="checkbox"
                        >
                            <Column selectionMode="multiple" style={{ width: '5%' }} />
                            <Column header="Sr." body={(_, { rowIndex }) => rowIndex + 1} style={{ width: '6%' }} />
                            <Column field="FeeTypeName" header="Particulars" style={{ width: '20%' }} />
                            <Column header="Due Date" body={(row) => row.DueDate ? moment(row.DueDate).format('DD/MM/YYYY') : ''} style={{ width: '12%' }} />
                            <Column header="Amount" body={(row) => `₹ ${row.Amount?.toLocaleString('en-IN')}`} style={{ width: '12%' }} />
                            <Column header="Paid" body={(row) => `₹ ${row.FeePaid?.toLocaleString('en-IN')}`} style={{ width: '12%' }} />
                            <Column header="Outstanding" body={(row) => `₹ ${row.Outstanding?.toLocaleString('en-IN')}`} style={{ width: '13%' }} />
                            <Column header="Status" body={(row) => (
                                <Tag value={row.Status} severity={row.Status === 'Paid' ? 'success' : row.Status === 'Partial' ? 'warning' : 'danger'} />
                            )} style={{ width: '10%' }} />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            ))}

            {/* Pay Buttons */}
            {selectedAllocations.length > 0 && (
                <div className="fp-pay-bar">
                    <div>
                        <span style={{ fontWeight: 600, color: '#666' }}>Selected: {selectedAllocations.length} items</span>
                        <span style={{ fontWeight: 700, fontSize: '18px', color: '#1C515E', marginLeft: '16px' }}>
                            Total: &#8377; {totalSelected.toLocaleString('en-IN')}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {PaymentConfig?.IsManualPaymentEnabled && (
                            <Button icon="pi pi-upload" label="Pay Manual" className="p-button-outlined" onClick={handleManualPayment} />
                        )}
                        {PaymentConfig?.IsOnlinePaymentEnabled && (
                            <Button icon="pi pi-credit-card" label="Pay Online" className="p-button-primary" onClick={handleOnlinePayment} />
                        )}
                    </div>
                </div>
            )}

            {/* Manual Payment Dialog */}
            <Dialog visible={showManualPay} onHide={() => setShowManualPay(false)} header="Manual Payment" style={{ width: '500px' }} draggable={false} dismissableMask>
                <div className="p-grid">
                    <div className="p-col-12 p-mb-2">
                        <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Amount to Pay</span>
                                <span style={{ fontWeight: 700, color: '#1C515E', fontSize: '18px' }}>&#8377; {totalSelected.toLocaleString('en-IN')}</span>
                            </div>
                        </div>
                    </div>
                    <div className="p-col-12 p-mb-2">
                        <label style={{ fontWeight: 600, fontSize: '13px' }}>Payment Date *</label>
                        <Calendar className="p-w-100 p-mt-1" value={paymentDate} onChange={(e) => setPaymentDate(e.value)} dateFormat="dd/mm/yy" showIcon />
                    </div>
                    <div className="p-col-12 p-mb-2" style={{ height: '200px' }}>
                        <UploadFile
                            label='Payment Proof *'
                            note=''
                            preview={proofPreview}
                            original={null}
                            uploadHandler={(e) => {
                                const file = e.files[0];
                                if (file) {
                                    setProofFile(file);
                                    setProofPreview(URL.createObjectURL(file));
                                }
                            }}
                            uploadRef={proofUploadRef}
                            handleDelete={() => { setProofFile(null); setProofPreview(null); }}
                            isEditAllow={true}
                            aspectRatio={null}
                        />
                    </div>
                    <div className="p-col-12 p-mb-2">
                        <label style={{ fontWeight: 600, fontSize: '13px' }}>Remarks</label>
                        <InputTextarea className="p-w-100 p-mt-1" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} placeholder="Optional remarks" />
                    </div>
                    <div className="p-col-12 p-d-flex p-jc-end" style={{ gap: '10px' }}>
                        <Button label="Cancel" className="p-button-secondary" onClick={() => setShowManualPay(false)} />
                        <Button label="Submit for Approval" icon="pi pi-check" className="p-button-primary" onClick={submitManualPayment} loading={saving} />
                    </div>
                </div>
            </Dialog>

            {/* Payment History Dialog */}
            <Dialog visible={showHistory} onHide={() => setShowHistory(false)} header="Payment History" style={{ width: '70vw', maxWidth: '800px' }} draggable={false} dismissableMask>
                <DataTable value={paymentHistory} size="small" className="custom-table" emptyMessage="No payments found">
                    <Column header="Sr." body={(_, { rowIndex }) => rowIndex + 1} style={{ width: '6%' }} />
                    <Column field="ReceiptNo" header="Receipt No." style={{ width: '12%' }} />
                    <Column header="Date" body={(row) => moment(row.PaymentDate).format('DD/MM/YYYY')} style={{ width: '12%' }} />
                    <Column field="PaymentMethod" header="Method" style={{ width: '10%' }} />
                    <Column header="Amount" body={(row) => `₹ ${(row.NetAmount || 0).toLocaleString('en-IN')}`} style={{ width: '15%' }} />
                    <Column header="Status" body={(row) => (
                        <Tag value={row.ApprovalStatus} severity={row.ApprovalStatus === 'Approved' ? 'success' : row.ApprovalStatus === 'Pending' ? 'warning' : 'danger'} />
                    )} style={{ width: '12%' }} />
                    <Column header="Action" body={(row) => (
                        row.ApprovalStatus === 'Approved' ? (
                            <Button icon="pi pi-print" label="Print" className="p-button-sm p-button-outlined"
                                onClick={() => { setShowHistory(false); setReceiptPaymentId(row._id); setShowReceipt(true); }} />
                        ) : row.ApprovalStatus === 'Rejected' ? (
                            <span style={{ color: '#c53030', fontSize: '12px' }}>{row.RejectionReason || 'Rejected'}</span>
                        ) : <span style={{ color: '#c05621', fontSize: '12px' }}>Awaiting approval</span>
                    )} style={{ width: '20%' }} />
                </DataTable>
            </Dialog>

            {/* Receipt Dialog */}
            <Dialog visible={showReceipt} onHide={() => { setShowReceipt(false); setReceiptPaymentId(null); }}
                header="Fee Receipt" style={{ width: '85vw', maxWidth: '900px' }} draggable={false} dismissableMask>
                {receiptPaymentId && (
                    <FeeReceiptPrint paymentId={receiptPaymentId} schoolId={schoolId} onClose={() => { setShowReceipt(false); setReceiptPaymentId(null); }} />
                )}
            </Dialog>
        </div>
    );
};

export default FeePayment;
